// Package imports:
import React, { useState, useEffect } from 'react';
import cx from 'classnames';
// Component imports:
import CompanyWatchButton from '../CompanyWatchButton/CompanyWatchButton';
import Icon from '../../Header/CustomIcon/CustomIcon';
// Service imports:
import useComponentVisible from '../../../services/hooks';
// Type imports:
import { ICompanyViewModel } from '../../../types/CompanyTypes';

const CompanyNavBar: React.FC<ICompanyViewModel> = ({
    id,
    isAuthenticated,
    isSubscriber
}) => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible();

    // State to track current URL path (pathname + hash)
    const [currentPath, setCurrentPath] = useState('');

    /**
     * Updates the current path state when the component mounts or URL changes
     * This combines the pathname and hash to get the full URL path
     */
    useEffect(() => {
        // Function to update the current path from window.location
        const updatePath = () => {
            setCurrentPath(`${window.location.pathname}${window.location.hash}`);
        };

        // Set initial path on component mount
        updatePath();

        // Listen for URL changes (when user navigates using browser back/forward buttons)
        window.addEventListener('popstate', updatePath);

        // Listen for hash changes (when hash part of URL changes)
        window.addEventListener('hashchange', updatePath);

        // Cleanup: remove event listeners when component unmounts
        return () => {
            window.removeEventListener('popstate', updatePath);
            window.removeEventListener('hashchange', updatePath);
        };
    }, []);

    /**
     * Checks if the current URL matches a given path pattern
     * @param path - The path to match against (can include hash)
     * @returns boolean - true if the current URL starts with the given path
     */
    const isActive = (path: string): boolean => {
        return currentPath.startsWith(path);
    };

    return (
        <div className="KCL_sub-navbar">
            {/* Mobile menu version */}
            <ul className="mobileMenu">
                <li>
                    <span
                        className='list-category'
                        id="mobile"
                        onClick={() => setIsComponentVisible(!isComponentVisible)}
                    >
                        Yfirlit fyrirtækis <Icon type='dropdown-arrow' />
                    </span>
                    <ul ref={ref} className={cx("mobile dropdown", { "focused": isComponentVisible })}>
                        {/* Company overview link - active when on /Fyrirtaeki/Yfirlit/{id} */}
                        <li>
                            <a
                                href={`/Fyrirtaeki/Yfirlit/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Yfirlit/${id}`) })}
                            >
                                Yfirlit fyrirtækis
                            </a>
                        </li>

                        {/* Reports section */}
                        <li className='list'>
                            <span>Skýrslur</span>
                            <ul className='list-items'>
                                {/* Annual reports - active when on /Fyrirtaeki/Arsreikningar/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Arsreikningar/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Arsreikningar/${id}`) })}
                                    >
                                        Ársreikningaskrá
                                    </a>
                                </li>
                                {/* Company registry - active when on /Fyrirtaeki/Hlutafelagaskra/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Hlutafelagaskra/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Hlutafelagaskra/${id}`) })}
                                    >
                                        Hlutafélagaskrá
                                    </a>
                                </li>
                                {/* Business registry - active when on /Fyrirtaeki/Fyrirtaekjaskra/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Fyrirtaekjaskra/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Fyrirtaekjaskra/${id}`) })}
                                    >
                                        Fyrirtækjaskrá
                                    </a>
                                </li>
                                {/* Entered annual reports - active when on /Fyrirtaeki/Innslegnir-Arsreikningar/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`) })}
                                    >
                                        Innslegnir ársreikningar
                                    </a>
                                </li>
                                {/* Comparison - active when on /Fyrirtaeki/Samanburdur/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Samanburdur/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Samanburdur/${id}`) })}
                                    >
                                        Samanburður
                                    </a>
                                </li>
                            </ul>
                        </li>

                        {/* Assets section */}
                        <li className='list'>
                            <span>Eignir</span>
                            <ul className='list-items'>
                                {/* Real estate - active when on /Fyrirtaeki/Fasteignir/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Fasteignir/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Fasteignir/${id}`) })}
                                    >
                                        Fasteignir
                                    </a>
                                </li>
                                {/* Vehicles - active when on /Fyrirtaeki/Okutaeki/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Okutaeki/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Okutaeki/${id}`) })}
                                    >
                                        Ökutæki
                                    </a>
                                </li>
                                {/* Machinery - active when on /Fyrirtaeki/Vinnuvelar/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Vinnuvelar/${id}`}
                                        className={cx({ 'active': isActive(`/Fyrirtaeki/Vinnuvelar/${id}`) })}
                                    >
                                        Vinnuvélar
                                    </a>
                                </li>
                            </ul>
                        </li>

                        {/* Legal publications - active when on /Fyrirtaeki/Logbirtingar/{id} */}
                        <li>
                            <a
                                href={`/Fyrirtaeki/Logbirtingar/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Logbirtingar/${id}`) })}
                            >
                                Lögbirtingar
                            </a>
                        </li>

                        {/* Valuation - active when on /Fyrirtaeki/Verdmat/{id} */}
                        <li>
                            <a
                                href={`/Fyrirtaeki/Verdmat/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Verdmat/${id}`) })}
                            >
                                Verðmat
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>

            {/* Desktop menu version */}
            <ul className='itemList'>
                {/* Company overview link */}
                <li>
                    <a
                        className={cx('list-category', { 'active': isActive(`/Fyrirtaeki/Yfirlit/${id}`) })}
                        href={`/Fyrirtaeki/Yfirlit/${id}`}
                    >
                        Yfirlit fyrirtækis
                    </a>
                </li>

                {/* Reports dropdown - parent is active if any child report page is active */}
                <li>
                    <span
                        className={cx('list-category', {
                            'active': isActive(`/Fyrirtaeki/Arsreikningar/${id}`) ||
                                isActive(`/Fyrirtaeki/Hlutafelagaskra/${id}`) ||
                                isActive(`/Fyrirtaeki/Fyrirtaekjaskra/${id}`) ||
                                isActive(`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`) ||
                                isActive(`/Fyrirtaeki/Samanburdur/${id}`)
                        })}
                    >
                        Skýrslur <span className='icon'><Icon type='dropdown-arrow' /></span>
                    </span>
                    <ul className="dropdown">
                        <li>
                            <a
                                href={`/Fyrirtaeki/Arsreikningar/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Arsreikningar/${id}`) })}
                            >
                                Ársreikningaskrá
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Hlutafelagaskra/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Hlutafelagaskra/${id}`) })}
                            >
                                Hlutafélagaskrá
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Fyrirtaekjaskra/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Fyrirtaekjaskra/${id}`) })}
                            >
                                Fyrirtækjaskrá
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`) })}
                            >
                                Innslegnir ársreikningar
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Samanburdur/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Samanburdur/${id}`) })}
                            >
                                Samanburður
                            </a>
                        </li>
                    </ul>
                </li>

                {/* Assets dropdown - parent is active if any child asset page is active */}
                <li>
                    <span
                        className={cx('list-category', {
                            'active': isActive(`/Fyrirtaeki/Fasteignir/${id}`) ||
                                isActive(`/Fyrirtaeki/Okutaeki/${id}`) ||
                                isActive(`/Fyrirtaeki/Vinnuvelar/${id}`)
                        })}
                    >
                        Eignir <span className='icon'><Icon type='dropdown-arrow' /></span>
                    </span>
                    <ul className="dropdown">
                        <li>
                            <a
                                href={`/Fyrirtaeki/Fasteignir/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Fasteignir/${id}`) })}
                            >
                                Fasteignir
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Okutaeki/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Okutaeki/${id}`) })}
                            >
                                Ökutæki
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Vinnuvelar/${id}`}
                                className={cx({ 'active': isActive(`/Fyrirtaeki/Vinnuvelar/${id}`) })}
                            >
                                Vinnuvélar
                            </a>
                        </li>
                    </ul>
                </li>

                {/* Legal publications link */}
                <li>
                    <a
                        className={cx('list-category', { 'active': isActive(`/Fyrirtaeki/Logbirtingar/${id}`) })}
                        href={`/Fyrirtaeki/Logbirtingar/${id}`}
                    >
                        Lögbirtingar
                    </a>
                </li>

                {/* Valuation link */}
                <li>
                    <a
                        className={cx('list-category', { 'active': isActive(`/Fyrirtaeki/Verdmat/${id}`) })}
                        href={`/Fyrirtaeki/Verdmat/${id}`}
                    >
                        Verðmat
                    </a>
                </li>
            </ul>
            <CompanyWatchButton isAuthenticated={isAuthenticated} isSubscriber={isSubscriber} id={id ?? ''} />
        </div>
    );
};

export default CompanyNavBar;